import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  CalendarClock, Plus, Trash2, Play, Pause, RotateCw, X, Download,
  FileText, BarChart3, Award, Mail, Bell, Clock, Check, Layers, Pencil, ToggleLeft, ToggleRight,
  Activity, TrendingUp, Send, Zap
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FREQ_LABELS = { weekly: 'Weekly', monthly: 'Monthly', custom: 'Custom' };
const TYPE_LABELS = { budget_summary: 'Budget Summary', vendor_scorecard: 'Vendor Scorecard', purchase_order: 'Purchase Order' };
const TYPE_ICONS = { budget_summary: BarChart3, vendor_scorecard: Award, purchase_order: FileText };
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const PIE_COLORS = ['#FFC40C', '#18181b', '#71717a', '#a1a1aa', '#3b82f6'];
const FREQ_DISPLAY = { weekly: 'Weekly', monthly: 'Monthly', custom: 'Custom' };

const DashboardTab = ({ dashboard, formatDate }) => {
  const kpi = dashboard.kpi || {};
  const reportsByType = (dashboard.reports_by_type || []).map(r => ({ ...r, name: TYPE_LABELS[r.name] || r.name }));
  const heatmap = dashboard.heatmap || [];
  const topTemplates = dashboard.top_templates || [];
  const recentActivity = dashboard.recent_activity || [];
  const schedsByFreq = (dashboard.schedules_by_frequency || []).map(r => ({ ...r, name: FREQ_LABELS[r.name] || r.name }));
  const maxHeat = Math.max(1, ...heatmap.map(d => d.count));

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-cards">
        {[
          { label: 'Total Reports', value: kpi.total_reports || 0, icon: FileText, accent: 'bg-primary' },
          { label: 'Active Schedules', value: kpi.active_schedules || 0, icon: CalendarClock, accent: 'bg-zinc-900' },
          { label: 'Email Delivery', value: `${kpi.email_delivery_rate || 0}%`, icon: Send, accent: 'bg-green-600' },
          { label: 'Templates', value: kpi.template_count || 0, icon: Layers, accent: 'bg-blue-600' },
        ].map((card) => (
          <div key={card.label} className="border border-zinc-200 bg-white p-4" data-testid={`kpi-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{card.label}</p>
              <div className={`w-8 h-8 flex items-center justify-center ${card.accent}`}>
                <card.icon className="w-4 h-4 text-white" />
              </div>
            </div>
            <p className="text-2xl font-heading font-bold">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Reports by Type */}
        <div className="border border-zinc-200 bg-white p-4" data-testid="chart-reports-by-type">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Reports by Type</p>
          {reportsByType.length > 0 ? (
            <div className="flex items-center gap-4">
              <div className="w-40 h-40 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={reportsByType} cx="50%" cy="50%" outerRadius={65} innerRadius={35} dataKey="value" paddingAngle={2} strokeWidth={0}>
                      {reportsByType.map((entry, i) => <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e4e4e7' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2 flex-1">
                {reportsByType.map((item, i) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 flex-shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                    <span className="text-xs font-medium text-zinc-700 flex-1">{item.name}</span>
                    <span className="text-sm font-mono font-bold">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-zinc-400 py-8 text-center">No reports generated yet</p>
          )}
        </div>

        {/* Schedules by Frequency */}
        <div className="border border-zinc-200 bg-white p-4" data-testid="chart-schedules-by-freq">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Schedules by Frequency</p>
          {schedsByFreq.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={schedsByFreq} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e4e4e7' }} />
                <Bar dataKey="value" fill="#FFC40C" radius={[2, 2, 0, 0]} name="Schedules" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-zinc-400 py-8 text-center">No schedules created yet</p>
          )}
        </div>
      </div>

      {/* Activity Heatmap */}
      <div className="border border-zinc-200 bg-white p-4" data-testid="activity-heatmap">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Report Activity (Last 30 Days)</p>
          <p className="text-xs text-zinc-400 font-mono">{kpi.reports_last_30d || 0} reports</p>
        </div>
        <div className="flex gap-[3px] flex-wrap">
          {heatmap.map((day) => {
            const intensity = day.count / maxHeat;
            const bg = day.count === 0 ? '#f4f4f5'
              : intensity < 0.33 ? '#fef3c7'
              : intensity < 0.66 ? '#FFC40C'
              : '#b45309';
            return (
              <div key={day.date} title={`${day.day_label}: ${day.count} report${day.count !== 1 ? 's' : ''}`}
                data-testid={`heat-${day.date}`}
                className="w-7 h-7 sm:w-8 sm:h-8 border border-zinc-100 transition-colors hover:ring-2 hover:ring-zinc-400 relative group flex items-center justify-center"
                style={{ backgroundColor: bg }}>
                <span className={`text-[9px] font-mono font-bold ${day.count > 0 && intensity >= 0.66 ? 'text-white' : 'text-zinc-400'}`}>
                  {day.count > 0 ? day.count : ''}
                </span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-2 mt-2 justify-end">
          <span className="text-[10px] text-zinc-400">Less</span>
          {['#f4f4f5', '#fef3c7', '#FFC40C', '#b45309'].map((c) => (
            <div key={c} className="w-4 h-4 border border-zinc-200" style={{ backgroundColor: c }} />
          ))}
          <span className="text-[10px] text-zinc-400">More</span>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Templates */}
        <div className="border border-zinc-200 bg-white p-4" data-testid="top-templates">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Most Used Templates</p>
          {topTemplates.length > 0 ? (
            <div className="space-y-2">
              {topTemplates.map((t, i) => {
                const maxRuns = topTemplates[0]?.runs || 1;
                const pct = Math.max(8, (t.runs / maxRuns) * 100);
                return (
                  <div key={t.template_id} className="flex items-center gap-3" data-testid={`top-tmpl-${i}`}>
                    <span className="text-xs font-mono text-zinc-400 w-4">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className="text-sm font-bold truncate">{t.name}</p>
                        <span className="text-xs font-mono text-zinc-500 ml-2">{t.runs} runs</span>
                      </div>
                      <div className="h-1.5 bg-zinc-100 w-full">
                        <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-zinc-400 py-6 text-center">No templates used yet</p>
          )}
        </div>

        {/* Recent Activity */}
        <div className="border border-zinc-200 bg-white p-4" data-testid="recent-activity">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Recent Activity</p>
          {recentActivity.length > 0 ? (
            <div className="space-y-2">
              {recentActivity.map((a) => {
                const TypeIcon = TYPE_ICONS[a.report_type] || FileText;
                return (
                  <div key={a.log_id} className="flex items-center gap-2.5 py-1.5 border-b border-zinc-100 last:border-0"
                       data-testid={`activity-${a.log_id}`}>
                    <div className="w-7 h-7 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                      <TypeIcon className="w-3.5 h-3.5 text-zinc-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate">{a.name}</p>
                      <p className="text-[10px] text-zinc-400">{formatDate(a.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {a.email_sent ? (
                        <span className="flex items-center gap-0.5 text-[10px] text-green-600"><Check className="w-3 h-3" />Sent</span>
                      ) : (
                        <span className="flex items-center gap-0.5 text-[10px] text-zinc-400"><Bell className="w-3 h-3" />Local</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-zinc-400 py-6 text-center">No activity yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportSchedulesPage = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sectionsDef, setSectionsDef] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);

  const [form, setForm] = useState({
    report_type: 'vendor_scorecard', project_id: '', order_id: '',
    frequency: 'weekly', day_of_week: 0, day_of_month: 1, interval_days: 14,
    recipients: '', delivery_method: 'both', name: '', template_id: '',
  });

  const [tmplForm, setTmplForm] = useState({
    name: '', report_type: 'vendor_scorecard', sections: {},
  });

  const canManage = user?.role === 'Owner' || user?.role === 'Project Manager';

  const fetchAll = useCallback(async () => {
    try {
      const [schedRes, logRes, projRes, emailRes, tmplRes, secRes, dashRes] = await Promise.all([
        axios.get(`${API}/report-schedules/`, { withCredentials: true }),
        axios.get(`${API}/report-schedules/logs`, { withCredentials: true }),
        axios.get(`${API}/projects/`, { withCredentials: true }),
        axios.get(`${API}/report-schedules/email-status`, { withCredentials: true }),
        axios.get(`${API}/report-schedules/templates`, { withCredentials: true }),
        axios.get(`${API}/report-schedules/templates/sections`, { withCredentials: true }),
        axios.get(`${API}/report-schedules/dashboard`, { withCredentials: true }),
      ]);
      setSchedules(schedRes.data);
      setLogs(logRes.data);
      setProjects(projRes.data);
      setEmailConfigured(emailRes.data.email_configured);
      setTemplates(tmplRes.data);
      setSectionsDef(secRes.data);
      setDashboard(dashRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (form.report_type === 'purchase_order' && form.project_id) {
      axios.get(`${API}/orders/?project_id=${form.project_id}`, { withCredentials: true })
        .then(res => setOrders(res.data || []))
        .catch(() => setOrders([]));
    }
  }, [form.report_type, form.project_id]);

  // Initialize template form sections when report_type changes
  useEffect(() => {
    if (sectionsDef[tmplForm.report_type]) {
      const defaults = {};
      Object.entries(sectionsDef[tmplForm.report_type]).forEach(([key, meta]) => {
        defaults[key] = editTemplate?.sections?.[key] ?? meta.default;
      });
      setTmplForm(prev => ({ ...prev, sections: defaults }));
    }
  }, [tmplForm.report_type, sectionsDef, editTemplate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const recipients = form.recipients.split(',').map(r => r.trim()).filter(Boolean);
    if (!recipients.length) { toast.error('Add at least one recipient email'); return; }

    const payload = {
      report_type: form.report_type, frequency: form.frequency,
      recipients, delivery_method: form.delivery_method,
      name: form.name || undefined,
      template_id: form.template_id || undefined,
    };
    if (form.report_type === 'budget_summary') payload.project_id = form.project_id;
    if (form.report_type === 'purchase_order') { payload.project_id = form.project_id; payload.order_id = form.order_id; }
    if (form.frequency === 'weekly') payload.day_of_week = form.day_of_week;
    if (form.frequency === 'monthly') payload.day_of_month = form.day_of_month;
    if (form.frequency === 'custom') payload.interval_days = form.interval_days;

    try {
      await axios.post(`${API}/report-schedules/`, payload, { withCredentials: true });
      toast.success('Schedule created');
      setShowCreate(false);
      setForm({ report_type: 'vendor_scorecard', project_id: '', order_id: '', frequency: 'weekly', day_of_week: 0, day_of_month: 1, interval_days: 14, recipients: '', delivery_method: 'both', name: '', template_id: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!tmplForm.name.trim()) { toast.error('Template name required'); return; }
    try {
      if (editTemplate) {
        await axios.put(`${API}/report-schedules/templates/${editTemplate.template_id}`, {
          name: tmplForm.name, sections: tmplForm.sections,
        }, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/report-schedules/templates`, tmplForm, { withCredentials: true });
        toast.success('Template created');
      }
      setShowTemplateModal(false);
      setEditTemplate(null);
      setTmplForm({ name: '', report_type: 'vendor_scorecard', sections: {} });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template? Schedules using it will revert to defaults.')) return;
    try {
      await axios.delete(`${API}/report-schedules/templates/${id}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const openEditTemplate = (tmpl) => {
    setEditTemplate(tmpl);
    setTmplForm({ name: tmpl.name, report_type: tmpl.report_type, sections: { ...tmpl.sections } });
    setShowTemplateModal(true);
  };

  const openNewTemplate = () => {
    setEditTemplate(null);
    setTmplForm({ name: '', report_type: 'vendor_scorecard', sections: {} });
    setShowTemplateModal(true);
  };

  const toggleStatus = async (s) => {
    const newStatus = s.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/report-schedules/${s.schedule_id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Schedule ${newStatus}`);
      fetchAll();
    } catch { toast.error('Failed to update'); }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await axios.delete(`${API}/report-schedules/${id}`, { withCredentials: true });
      toast.success('Schedule deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const runNow = async (id) => {
    try {
      await axios.post(`${API}/report-schedules/${id}/run-now`, {}, { withCredentials: true });
      toast.success('Report generation triggered');
      setTimeout(fetchAll, 3000);
    } catch { toast.error('Failed to trigger'); }
  };

  const downloadLog = async (logId, filename) => {
    try {
      const res = await axios.get(`${API}/report-schedules/logs/${logId}/download`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const formatDate = (d) => {
    if (!d) return '\u2014';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const filteredTemplates = templates.filter(t =>
    activeTab === 'templates' ? true : t.report_type === form.report_type
  );

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="schedules-loading">
        <div className="animate-pulse space-y-3 w-full max-w-2xl px-6">
          {[1,2,3].map(i => <div key={i} className="h-20 bg-zinc-200 border border-zinc-300" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-5xl mx-auto" data-testid="report-schedules-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold uppercase tracking-wide" data-testid="page-title">
            Report Schedules
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Automate delivery of Budget, Scorecard, and PO reports</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button onClick={openNewTemplate} data-testid="create-template-btn"
              className="inline-flex items-center gap-2 h-12 px-5 bg-zinc-900 text-white font-bold uppercase border border-zinc-200 hover:bg-zinc-800 active:translate-y-0.5 transition-all">
              <Layers className="w-4 h-4" /> Template
            </button>
            <button onClick={() => setShowCreate(true)} data-testid="create-schedule-btn"
              className="inline-flex items-center gap-2 h-12 px-6 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-primary-hover active:translate-y-0.5 transition-all">
              <Plus className="w-5 h-5" /> Schedule
            </button>
          </div>
        )}
      </div>

      {/* Email warning */}
      {!emailConfigured && (
        <div className="bg-amber-50 border border-amber-200 p-3 mb-4 flex items-start gap-3" data-testid="email-warning">
          <Mail className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-800">Email delivery not configured</p>
            <p className="text-xs text-amber-600">Set RESEND_API_KEY in backend .env to enable email delivery. In-app downloads still work.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b-2 border-zinc-200 mb-6">
        {[
          { key: 'dashboard', label: 'Dashboard', icon: Activity, count: null },
          { key: 'schedules', label: 'Schedules', icon: CalendarClock, count: schedules.length },
          { key: 'templates', label: 'Templates', icon: Layers, count: templates.length },
          { key: 'history', label: 'History', icon: Clock, count: logs.length },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`px-4 py-2.5 text-xs font-heading font-bold uppercase tracking-wider border-b-2 -mb-[2px] transition-colors ${
              activeTab === tab.key ? 'border-primary text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'
            }`}>
            <tab.icon className="w-4 h-4 inline mr-1.5 -mt-0.5" />{tab.label}{tab.count !== null ? ` (${tab.count})` : ''}
          </button>
        ))}
      </div>

      {/* ====== DASHBOARD TAB ====== */}
      {activeTab === 'dashboard' && dashboard && (
        <DashboardTab dashboard={dashboard} formatDate={formatDate} />
      )}

      {/* ====== SCHEDULES TAB ====== */}
      {activeTab === 'schedules' && (
        <div className="space-y-3" data-testid="schedules-list">
          {schedules.length === 0 ? (
            <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="schedules-empty">
              <CalendarClock className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
              <p className="font-heading font-bold uppercase text-zinc-400 mb-1">No Schedules Yet</p>
              <p className="text-sm text-zinc-400">Create a schedule to automate report delivery</p>
            </div>
          ) : schedules.map(s => {
            const TypeIcon = TYPE_ICONS[s.report_type] || FileText;
            return (
              <div key={s.schedule_id} className={`border bg-white p-4 sm:p-5 ${s.status === 'paused' ? 'border-zinc-300 opacity-70' : 'border-zinc-200'}`}
                   data-testid={`schedule-card-${s.schedule_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${s.status === 'active' ? 'bg-primary' : 'bg-zinc-200'}`}>
                      <TypeIcon className="w-5 h-5 text-zinc-900" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-bold uppercase text-sm tracking-wide truncate">{s.name}</h3>
                        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${
                          s.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-zinc-100 text-zinc-500'
                        }`}>{s.status}</span>
                        {s.template_name && (
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-blue-50 text-blue-700 flex items-center gap-1">
                            <Layers className="w-3 h-3" />{s.template_name}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-zinc-500">
                        <span className="font-mono">{FREQ_LABELS[s.frequency] || s.frequency}
                          {s.frequency === 'weekly' && s.day_of_week != null && ` \u00b7 ${DAYS[s.day_of_week]}`}
                          {s.frequency === 'monthly' && s.day_of_month && ` \u00b7 Day ${s.day_of_month}`}
                          {s.frequency === 'custom' && s.interval_days && ` \u00b7 Every ${s.interval_days}d`}
                        </span>
                        <span>{s.recipients?.length || 0} recipient{(s.recipients?.length || 0) !== 1 ? 's' : ''}</span>
                        {s.project_name && <span className="truncate max-w-[200px]">{s.project_name}</span>}
                        {s.order_label && <span className="truncate max-w-[200px]">{s.order_label}</span>}
                      </div>
                      <div className="flex gap-x-4 mt-1 text-[11px] text-zinc-400">
                        <span>Next: {formatDate(s.next_run)}</span>
                        {s.last_run && <span>Last: {formatDate(s.last_run)}</span>}
                        <span>Runs: {s.run_count || 0}</span>
                      </div>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => runNow(s.schedule_id)} title="Run now" data-testid={`run-now-${s.schedule_id}`}
                        className="w-9 h-9 flex items-center justify-center border border-zinc-200 hover:bg-zinc-50 transition-colors">
                        <RotateCw className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggleStatus(s)} title={s.status === 'active' ? 'Pause' : 'Resume'} data-testid={`toggle-${s.schedule_id}`}
                        className="w-9 h-9 flex items-center justify-center border border-zinc-200 hover:bg-zinc-50 transition-colors">
                        {s.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button onClick={() => deleteSchedule(s.schedule_id)} title="Delete" data-testid={`delete-${s.schedule_id}`}
                        className="w-9 h-9 flex items-center justify-center border border-zinc-200 hover:bg-red-50 text-zinc-500 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ====== TEMPLATES TAB ====== */}
      {activeTab === 'templates' && (
        <div className="space-y-3" data-testid="templates-list">
          {templates.length === 0 ? (
            <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="templates-empty">
              <Layers className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
              <p className="font-heading font-bold uppercase text-zinc-400 mb-1">No Templates Yet</p>
              <p className="text-sm text-zinc-400">Templates let you customize which sections appear in reports</p>
            </div>
          ) : templates.map(tmpl => {
            const TypeIcon = TYPE_ICONS[tmpl.report_type] || FileText;
            const enabledCount = Object.values(tmpl.sections || {}).filter(Boolean).length;
            const totalCount = Object.keys(tmpl.sections || {}).length;
            return (
              <div key={tmpl.template_id} className="border border-zinc-200 bg-white p-4 sm:p-5" data-testid={`template-card-${tmpl.template_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <TypeIcon className="w-5 h-5 text-blue-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-bold uppercase text-sm tracking-wide truncate">{tmpl.name}</h3>
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-zinc-100 text-zinc-600">
                          {TYPE_LABELS[tmpl.report_type]}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sectionsDef[tmpl.report_type] && Object.entries(sectionsDef[tmpl.report_type]).map(([key, meta]) => (
                          <span key={key} className={`px-2 py-0.5 text-[10px] font-bold uppercase ${
                            tmpl.sections?.[key] ? 'bg-green-50 text-green-700' : 'bg-zinc-50 text-zinc-400 line-through'
                          }`}>{meta.label}</span>
                        ))}
                      </div>
                      <p className="text-[11px] text-zinc-400 mt-1.5">{enabledCount}/{totalCount} sections enabled</p>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => openEditTemplate(tmpl)} title="Edit" data-testid={`edit-template-${tmpl.template_id}`}
                        className="w-9 h-9 flex items-center justify-center border border-zinc-200 hover:bg-zinc-50 transition-colors">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteTemplate(tmpl.template_id)} title="Delete" data-testid={`delete-template-${tmpl.template_id}`}
                        className="w-9 h-9 flex items-center justify-center border border-zinc-200 hover:bg-red-50 text-zinc-500 hover:text-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ====== HISTORY TAB ====== */}
      {activeTab === 'history' && (
        <div className="space-y-2" data-testid="history-list">
          {logs.length === 0 ? (
            <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="history-empty">
              <Clock className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
              <p className="font-heading font-bold uppercase text-zinc-400 mb-1">No Report History</p>
              <p className="text-sm text-zinc-400">Reports will appear here after schedules run</p>
            </div>
          ) : logs.map(log => (
            <div key={log.log_id} className="border border-zinc-200 bg-white p-3 sm:p-4 flex items-center gap-3"
                 data-testid={`log-${log.log_id}`}>
              <div className="w-9 h-9 bg-zinc-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-zinc-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{log.name}</p>
                <div className="flex items-center gap-3 text-[11px] text-zinc-400 mt-0.5">
                  <span>{formatDate(log.created_at)}</span>
                  <span className="flex items-center gap-1">
                    {log.email_sent ? <><Check className="w-3 h-3 text-green-500" />Email sent</> : <><Bell className="w-3 h-3" />In-app only</>}
                  </span>
                  <span>{log.recipients?.length || 0} recipient{(log.recipients?.length || 0) !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <button onClick={() => downloadLog(log.log_id, log.filename)}
                data-testid={`download-log-${log.log_id}`}
                className="inline-flex items-center gap-1.5 h-9 px-3 bg-zinc-900 text-white text-xs font-bold uppercase border border-zinc-200 hover:bg-zinc-800 transition-all flex-shrink-0">
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ====== CREATE SCHEDULE MODAL ====== */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-12 sm:pt-20 px-4" data-testid="create-modal-overlay">
          <div className="bg-white border-2 border-zinc-900 w-full max-w-lg max-h-[80vh] overflow-y-auto" data-testid="create-modal">
            <div className="bg-primary px-5 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="font-heading font-bold uppercase tracking-wide text-zinc-900">New Report Schedule</h2>
              <button onClick={() => setShowCreate(false)} data-testid="close-modal-btn"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Report Type</label>
                <select value={form.report_type} onChange={e => setForm({...form, report_type: e.target.value, project_id: '', order_id: '', template_id: ''})}
                  data-testid="select-report-type"
                  className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none">
                  <option value="vendor_scorecard">Vendor Scorecard</option>
                  <option value="budget_summary">Budget Summary</option>
                  <option value="purchase_order">Purchase Order</option>
                </select>
              </div>

              {/* Template selector */}
              {templates.filter(t => t.report_type === form.report_type).length > 0 && (
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">
                    Template <span className="font-normal text-zinc-400">(optional)</span>
                  </label>
                  <select value={form.template_id} onChange={e => setForm({...form, template_id: e.target.value})}
                    data-testid="select-template"
                    className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none">
                    <option value="">Default (all sections)</option>
                    {templates.filter(t => t.report_type === form.report_type).map(t => (
                      <option key={t.template_id} value={t.template_id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(form.report_type === 'budget_summary' || form.report_type === 'purchase_order') && (
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Project</label>
                  <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value, order_id: ''})}
                    data-testid="select-project"
                    className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none" required>
                    <option value="">Select project...</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                </div>
              )}

              {form.report_type === 'purchase_order' && form.project_id && (
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Order</label>
                  <select value={form.order_id} onChange={e => setForm({...form, order_id: e.target.value})}
                    data-testid="select-order"
                    className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none" required>
                    <option value="">Select order...</option>
                    {orders.map(o => <option key={o.order_id} value={o.order_id}>PO {o.po_number} - {o.vendor_name}</option>)}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Frequency</label>
                <select value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}
                  data-testid="select-frequency"
                  className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none">
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom Interval</option>
                </select>
              </div>

              {form.frequency === 'weekly' && (
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Day of Week</label>
                  <select value={form.day_of_week} onChange={e => setForm({...form, day_of_week: parseInt(e.target.value)})}
                    data-testid="select-day-of-week"
                    className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none">
                    {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
              )}
              {form.frequency === 'monthly' && (
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Day of Month</label>
                  <select value={form.day_of_month} onChange={e => setForm({...form, day_of_month: parseInt(e.target.value)})}
                    data-testid="select-day-of-month"
                    className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none">
                    {Array.from({length: 28}, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
                  </select>
                </div>
              )}
              {form.frequency === 'custom' && (
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Interval (Days)</label>
                  <input type="number" min="1" max="365" value={form.interval_days}
                    onChange={e => setForm({...form, interval_days: parseInt(e.target.value) || 14})}
                    data-testid="input-interval-days"
                    className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-mono focus:border-primary focus:outline-none" />
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Recipients (comma-separated emails)</label>
                <input type="text" value={form.recipients}
                  onChange={e => setForm({...form, recipients: e.target.value})}
                  placeholder="pm@company.com, boss@company.com"
                  data-testid="input-recipients"
                  className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm focus:border-primary focus:outline-none" required />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Delivery Method</label>
                <select value={form.delivery_method} onChange={e => setForm({...form, delivery_method: e.target.value})}
                  data-testid="select-delivery"
                  className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none">
                  <option value="both">Email + In-App</option>
                  <option value="email">Email Only</option>
                  <option value="in_app">In-App Only</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Schedule Name (optional)</label>
                <input type="text" value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="e.g. Weekly Vendor Report for Leadership"
                  data-testid="input-name"
                  className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm focus:border-primary focus:outline-none" />
              </div>

              <button type="submit" data-testid="submit-schedule-btn"
                className="w-full h-12 bg-primary text-zinc-900 font-bold uppercase tracking-wide border-2 border-zinc-900 hover:bg-primary-hover active:translate-y-0.5 transition-all">
                Create Schedule
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ====== TEMPLATE MODAL ====== */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-12 sm:pt-20 px-4" data-testid="template-modal-overlay">
          <div className="bg-white border-2 border-zinc-900 w-full max-w-lg max-h-[80vh] overflow-y-auto" data-testid="template-modal">
            <div className="bg-zinc-900 px-5 py-4 flex items-center justify-between sticky top-0 z-10">
              <h2 className="font-heading font-bold uppercase tracking-wide text-white">
                {editTemplate ? 'Edit Template' : 'New Template'}
              </h2>
              <button onClick={() => { setShowTemplateModal(false); setEditTemplate(null); }} data-testid="close-template-modal-btn">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <form onSubmit={handleSaveTemplate} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Template Name</label>
                <input type="text" value={tmplForm.name}
                  onChange={e => setTmplForm({...tmplForm, name: e.target.value})}
                  placeholder="e.g. Executive Summary Only"
                  data-testid="input-template-name"
                  className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm focus:border-primary focus:outline-none" required />
              </div>

              {!editTemplate && (
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1.5">Report Type</label>
                  <select value={tmplForm.report_type} onChange={e => setTmplForm({...tmplForm, report_type: e.target.value, sections: {}})}
                    data-testid="select-template-type"
                    className="w-full h-11 px-3 border-2 border-zinc-300 bg-white text-sm font-medium focus:border-primary focus:outline-none">
                    <option value="vendor_scorecard">Vendor Scorecard</option>
                    <option value="budget_summary">Budget Summary</option>
                    <option value="purchase_order">Purchase Order</option>
                  </select>
                </div>
              )}

              {/* Section toggles */}
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-3">Sections</label>
                <div className="space-y-2">
                  {sectionsDef[tmplForm.report_type] && Object.entries(sectionsDef[tmplForm.report_type]).map(([key, meta]) => {
                    const enabled = tmplForm.sections[key] ?? meta.default;
                    return (
                      <button type="button" key={key}
                        onClick={() => setTmplForm({...tmplForm, sections: {...tmplForm.sections, [key]: !enabled}})}
                        data-testid={`toggle-section-${key}`}
                        className={`w-full flex items-center justify-between p-3 border-2 transition-colors ${
                          enabled ? 'border-green-300 bg-green-50' : 'border-zinc-200 bg-zinc-50'
                        }`}>
                        <span className={`text-sm font-bold uppercase ${enabled ? 'text-green-800' : 'text-zinc-400'}`}>
                          {meta.label}
                        </span>
                        {enabled ?
                          <ToggleRight className="w-6 h-6 text-green-600" /> :
                          <ToggleLeft className="w-6 h-6 text-zinc-400" />
                        }
                      </button>
                    );
                  })}
                </div>
              </div>

              <button type="submit" data-testid="submit-template-btn"
                className="w-full h-12 bg-zinc-900 text-white font-bold uppercase tracking-wide border-2 border-zinc-900 hover:bg-zinc-800 active:translate-y-0.5 transition-all">
                {editTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportSchedulesPage;
